import { cloneDeep } from 'lodash-es';
import { initializeTargets } from './initializeTargets';
import { getDefaultWidgetSize } from './baseGridLayout.constants';
import { useWidgetUUIDStore } from '@/dashboard/stores/widget-uuid';
import { applyDefaultChartOptions } from '@/dashboard/features/widgets/common/domain/chartOption';
import { getWidgetCompName } from '@/dashboard/features/widgets/common/domain/widgetName';
import { isWidgetReferenceGlobalTimePeriod } from '@/dashboard/features/globalTimePeriod/domain/globalTimePeriod';
import {
  CUSTOM_KEY,
  DEFAULT_WIDGET_COLOR_OPTION,
  DEFAULT_WIDGET_TITLE_STYLE,
  DISPLAY_STYLE_KEY,
  getDisplayStyleList,
  getWidgetChartTypeList,
  WIDGET_CHART_TYPE_KEY,
  WIDGET_TYPE_KEY,
} from '../../widgets/common/constants/define';
import type { GridLayoutItem } from '@/dashboard/shared/types/widgets.types';
import { isLegendValidDataResource } from '../../widgets/common/domain/dataResource';
import { LayoutItem } from 'vue3-grid-layout-next/dist/helpers/utils';

export const mapGridLayoutItem = (
  gridLayoutItem: GridLayoutItem,
  callback: (item: GridLayoutItem, depth?: number) => GridLayoutItem,
  depth?: number,
): GridLayoutItem => {
  return callback(
    {
      ...gridLayoutItem,
      children: gridLayoutItem.children?.map((child) =>
        mapGridLayoutItem(child, callback, (depth ?? 0) + 1),
      ),
    },
    depth ?? 0,
  );
};

export const forEachGridLayoutItems = (
  gridLayoutItems: GridLayoutItem[],
  callback: (item: GridLayoutItem) => void,
): void => {
  gridLayoutItems.forEach((item) => {
    callback(item);
    item.children?.forEach((child) => forEachGridLayoutItems([child], callback));
  });
};

export const forEveryGridLayoutItems = (
  gridLayoutItems: GridLayoutItem[],
  callback: (item: GridLayoutItem) => boolean,
): boolean => {
  return gridLayoutItems.every((item) => {
    if (!callback(item)) return false;
    return !item.children || forEveryGridLayoutItems(item.children, callback);
  });
};

export const forSomeGridLayoutItems = (
  gridLayoutItems: GridLayoutItem[],
  callback: (item: GridLayoutItem) => boolean,
): boolean => {
  return gridLayoutItems.some((item) => {
    if (callback(item)) return true;
    return item.children ? forSomeGridLayoutItems(item.children, callback) : false;
  });
};

export const findTopLevelItemById = <T extends LayoutItem>(
  gridLayoutItems: T[],
  id: T['i'],
): T | undefined => {
  return gridLayoutItems.find((item) => item.i === id);
};

export const findIndexById = <T extends LayoutItem>(gridLayoutItems: T[], id: T['i']): number => {
  return gridLayoutItems.findIndex((item) => item.i === id);
};

export const findNestedItemById = (
  gridLayoutItems: GridLayoutItem[],
  id: GridLayoutItem['i'],
): GridLayoutItem | undefined => {
  return gridLayoutItems
    .flatMap((item) => (item.children ? findTopLevelItemById(item.children, id) : []))
    .find((item) => item !== undefined);
};

export const getNewGridLayoutItem = (
  item: Partial<GridLayoutItem>,
  isInitializeTargets = false,
  globalFilterIdList?: Set<number>,
  treeFilterIdList?: Set<number>,
): GridLayoutItem => {
  const widgetType = item.widgetType ?? WIDGET_TYPE_KEY.GRAPHS;
  const chartType = item.chartType ?? getWidgetChartTypeList()[widgetType][0].value;
  const resolvedDisplayStyle =
    item.displayStyle ?? getDisplayStyleList()[chartType]?.[0]?.value ?? '';
  // NOTE: 기존 scatter 위젯의 빈 displayStyle('')을 SCATTER_BASIC으로 마이그레이션
  const displayStyle =
    chartType === 'SCATTER' && resolvedDisplayStyle === ''
      ? DISPLAY_STYLE_KEY.SCATTER_BASIC
      : resolvedDisplayStyle;

  const { generateWidgetUUID } = useWidgetUUIDStore();
  const {
    minW: defaultMinW,
    minH: defaultMinH,
    h: defaultH,
    w: defaultW,
  } = getDefaultWidgetSize(chartType, displayStyle);

  const result: GridLayoutItem = {
    i: item.i ?? generateWidgetUUID(),
    x: item.x ?? 0,
    y: item.y ?? 0,
    w: item.w ?? defaultW,
    h: item.h ?? defaultH,
    widgetType,
    chartType,
    displayStyle,
    compName: getWidgetCompName(WIDGET_CHART_TYPE_KEY[chartType], DISPLAY_STYLE_KEY[displayStyle]),
    titleOption: item.titleOption ?? {
      showTitle: true,
      titleText: '',
      titleStyle: cloneDeep(DEFAULT_WIDGET_TITLE_STYLE),
    },
    widgetColor: item.widgetColor ?? cloneDeep(DEFAULT_WIDGET_COLOR_OPTION),
    createType: item.createType ?? 'base',
    headerType: item.headerType,
    timePeriod: item.timePeriod ?? 'globalTime',
    chartData: item.chartData,
    colorTheme: item.colorTheme ?? CUSTOM_KEY,
    children: item.children?.map((child) =>
      getNewGridLayoutItem(child, isInitializeTargets, globalFilterIdList, treeFilterIdList),
    ),
    minW: item.minW ?? defaultMinW,
    minH: item.minH ?? defaultMinH,
    chartOption: applyDefaultChartOptions(chartType, item.chartOption),
    filterOption: item.filterOption,
    calendarTimeRange: item.calendarTimeRange,
    isInViewport: item.isInViewport,
    customId: item.customId,
    originHeight: item.originHeight,
    widgetMappingId: item.widgetMappingId,
    dataResource: item.dataResource ?? 'default',
  };

  if (isInitializeTargets) {
    return initializeTargets(
      result,
      globalFilterIdList ?? new Set<number>(),
      treeFilterIdList ?? new Set<number>(),
    );
  }

  return result;
};

export const findGridLayoutItemById = (
  gridLayoutItems: GridLayoutItem[],
  id: GridLayoutItem['i'],
): GridLayoutItem | undefined => {
  for (const item of gridLayoutItems) {
    if (item.i === id) {
      return item;
    }
    if (item.children) {
      const foundItem = findGridLayoutItemById(item.children, id);
      if (foundItem) {
        return foundItem;
      }
    }
  }
  return undefined;
};

export const findAllGridLayoutItemById = (
  gridLayoutItems: GridLayoutItem[],
  id: GridLayoutItem['i'],
): GridLayoutItem[] => {
  const items: GridLayoutItem[] = [];

  for (const item of gridLayoutItems) {
    if (item.i === id) {
      items.push(item);
    }
    if (item.children) {
      const foundItems = findAllGridLayoutItemById(item.children, id);
      items.push(...foundItems);
    }
  }

  return items;
};

export const filterGridLayoutItem = (
  gridLayoutItem: GridLayoutItem[],
  filter: (item: GridLayoutItem) => boolean,
): GridLayoutItem[] => {
  return gridLayoutItem
    .map((item) => {
      if (!filter(item)) return null;

      const filteredChildren = item.children
        ? filterGridLayoutItem(item.children, filter)
        : undefined;

      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter(Boolean) as GridLayoutItem[];
};

export const isGridItemNotUseTimePeriod = ({
  widgetType,
  createType,
  timePeriod,
  chartOption,
  dataResource,
}: GridLayoutItem) => {
  if (dataResource !== 'default') {
    return false;
  }
  return (
    ['TEXT', 'IMAGE', 'GROUP', 'FILTER', 'MEMO'].includes(widgetType) ||
    (createType === 'reference' && !isWidgetReferenceGlobalTimePeriod(chartOption)) ||
    timePeriod !== 'globalTime'
  );
};

export const flattenWidgets = (items: GridLayoutItem[]): GridLayoutItem[] => {
  return items.reduce<GridLayoutItem[]>((acc, item) => {
    const { children, ...rest } = item;
    acc.push(rest);
    if (children?.length) {
      acc.push(...flattenWidgets(children));
    }
    return acc;
  }, []);
};

export const filterLegendValidWidget = (item: GridLayoutItem) => {
  switch (item.widgetType) {
    case 'FILTER':
    case 'MEMO':
    case 'IMAGE':
    case 'TEXT':
    case 'ETC':
      return false;
    default:
      break;
  }
  return isLegendValidDataResource(item.dataResource);
};
